import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { DocumentModal } from "@/pages/documents/DocumentModal";
import { ApiError } from "@/lib/api";
import { cn, EM_DASH } from "@/lib/utils";

import { formatMoney, formatQuantity } from "./suntronicInvoicesFormat";
import {
  createSuntronicInvoiceGipi,
  fetchSuntronicInvoiceGipiPurchaseOrderLines,
  fetchSuntronicInvoiceGipiPurchaseOrders,
  type SuntronicInvoiceGipiPurchaseOrderLineRow,
  type SuntronicInvoiceGipiPurchaseOrderRow,
} from "./suntronicInvoicesApi";

function describeMutationError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

function toNumber(value: number | string | null | undefined): number {
  if (value == null || value === "") return 0;
  const parsed = typeof value === "number" ? value : Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseQuantityInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const parsed = Number.parseFloat(trimmed.replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function formatSuggestedQuantity(value: number | string | null | undefined): string {
  const parsed = toNumber(value);
  return parsed > 0 ? String(parsed) : "";
}

export function SuntronicInvoiceCreateGipiModal({
  invoiceId,
  invoiceNumber,
  onClose,
  onCreated,
}: {
  invoiceId: number;
  invoiceNumber: string;
  onClose: () => void;
  onCreated: (result: { mv_document_id: number; purchase_invoice_id: number }) => void;
}): JSX.Element {
  const [search, setSearch] = useState("");
  const [selectedPurchaseOrderId, setSelectedPurchaseOrderId] = useState<number | null>(null);
  const [quantitiesByItemId, setQuantitiesByItemId] = useState<Record<number, string>>({});

  const purchaseOrdersQuery = useQuery({
    queryKey: ["accounting", "suntronic-invoices", "gipi-purchase-orders", invoiceId],
    queryFn: () => fetchSuntronicInvoiceGipiPurchaseOrders(invoiceId),
  });

  const linesQuery = useQuery({
    queryKey: ["accounting", "suntronic-invoices", "gipi-purchase-order-lines", invoiceId, selectedPurchaseOrderId],
    queryFn: () => fetchSuntronicInvoiceGipiPurchaseOrderLines(invoiceId, selectedPurchaseOrderId!),
    enabled: selectedPurchaseOrderId != null,
  });

  useEffect(() => {
    const rows = linesQuery.data;
    if (rows == null) {
      setQuantitiesByItemId({});
      return;
    }
    const next: Record<number, string> = {};
    for (const row of rows) {
      next[row.purchase_order_item_id] = formatSuggestedQuantity(row.suggested_quantity);
    }
    setQuantitiesByItemId(next);
  }, [linesQuery.data]);

  const filteredRows = useMemo(() => {
    const rows = purchaseOrdersQuery.data ?? [];
    const needle = search.trim().toLowerCase();
    if (needle === "") return rows;
    return rows.filter((row) =>
      [row.po_number, row.supplier_name, row.id]
        .map((value) => String(value ?? "").toLowerCase())
        .some((value) => value.includes(needle)),
    );
  }, [purchaseOrdersQuery.data, search]);

  const selected = filteredRows.find((row) => row.id === selectedPurchaseOrderId) ?? null;
  const lines = linesQuery.data ?? [];

  const requestedLines = useMemo(() => {
    const payload: Array<{ purchase_order_item_id: number; quantity: number }> = [];
    for (const row of lines) {
      const remaining = toNumber(row.quantity_remaining);
      const parsed = parseQuantityInput(quantitiesByItemId[row.purchase_order_item_id] ?? "");
      if (parsed == null || parsed <= 0) continue;
      if (parsed > remaining) continue;
      payload.push({ purchase_order_item_id: row.purchase_order_item_id, quantity: parsed });
    }
    return payload;
  }, [lines, quantitiesByItemId]);

  const quantityInvalid = useMemo(() => {
    for (const row of lines) {
      const raw = quantitiesByItemId[row.purchase_order_item_id] ?? "";
      if (raw.trim() === "") continue;
      const parsed = parseQuantityInput(raw);
      if (parsed == null || parsed < 0 || parsed > toNumber(row.quantity_remaining)) {
        return true;
      }
    }
    return false;
  }, [lines, quantitiesByItemId]);

  const createMutation = useMutation({
    mutationFn: async () => {
      if (selectedPurchaseOrderId == null) {
        throw new Error("Select a purchase order.");
      }
      if (requestedLines.length === 0) {
        throw new Error("Enter a quantity on at least one purchase-order line.");
      }
      return createSuntronicInvoiceGipi(invoiceId, selectedPurchaseOrderId, requestedLines);
    },
    onSuccess: (result) => {
      onCreated(result);
      onClose();
    },
  });

  return (
    <DocumentModal
      title="Create GIPI"
      description={`Pick an open purchase order, then confirm the line and quantity. You can create more than one GIPI when leftover sits on more than one purchase order. The GIPI will use invoice ${invoiceNumber}.`}
      onClose={onClose}
      showCloseButton={false}
      shellClassName="max-w-[min(90rem,calc(100vw-2rem))]"
    >
      <div className="space-y-4">
        <div className="grid gap-4 lg:grid-cols-[minmax(16rem,22rem)_minmax(0,1fr)]">
          <div className="min-w-0 space-y-3">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by purchase order number or supplier..."
              type="search"
            />
            {purchaseOrdersQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading purchase orders…
              </div>
            ) : purchaseOrdersQuery.isError ? (
              <p className="text-sm text-rose-700">Could not load purchase orders for this vendor.</p>
            ) : filteredRows.length === 0 ? (
              <p className="text-sm text-slate-600">No open, not-fully-received purchase orders for this vendor.</p>
            ) : (
              <div className="max-h-[min(28rem,55vh)] overflow-x-hidden overflow-y-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="whitespace-nowrap px-3 py-2">Purchase order</th>
                        <th className="whitespace-nowrap px-3 py-2">Supplier</th>
                        <th className="whitespace-nowrap px-3 py-2 text-right">Remaining</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map((row) => (
                        <PurchaseOrderPickerRow
                          key={row.id}
                          row={row}
                          selected={row.id === selectedPurchaseOrderId}
                          onSelect={() => setSelectedPurchaseOrderId(row.id)}
                        />
                      ))}
                    </tbody>
                  </table>
              </div>
            )}
          </div>

          <div className="min-w-0 space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">Purchase order lines</h3>
            {selectedPurchaseOrderId == null ? (
              <p className="text-sm text-slate-600">Select a purchase order to see its lines.</p>
            ) : linesQuery.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading lines…
              </div>
            ) : linesQuery.isError ? (
              <p className="text-sm text-rose-700">Could not load lines for that purchase order.</p>
            ) : lines.length === 0 ? (
              <p className="text-sm text-slate-600">That purchase order has no lines.</p>
            ) : (
              <div className="max-h-[min(28rem,55vh)] overflow-x-hidden overflow-y-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="whitespace-nowrap px-3 py-2">SKU</th>
                        <th className="min-w-[12rem] px-3 py-2">Description</th>
                        <th className="whitespace-nowrap px-3 py-2 text-right">Ordered</th>
                        <th className="whitespace-nowrap px-3 py-2 text-right">Remaining</th>
                        <th className="whitespace-nowrap px-3 py-2 text-right">Matched</th>
                        <th className="whitespace-nowrap px-3 py-2 text-right">Unit price</th>
                        <th className="whitespace-nowrap px-3 py-2 text-right">Quantity on GIPI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((row) => (
                        <PurchaseOrderLineRow
                          key={row.purchase_order_item_id}
                          row={row}
                          quantityRaw={quantitiesByItemId[row.purchase_order_item_id] ?? ""}
                          disabled={createMutation.isPending}
                          onQuantityChange={(value) => {
                            setQuantitiesByItemId((current) => ({
                              ...current,
                              [row.purchase_order_item_id]: value,
                            }));
                          }}
                        />
                      ))}
                    </tbody>
                  </table>
              </div>
            )}
          </div>
        </div>
        {createMutation.isError ? (
          <p className="text-sm text-rose-700">
            {describeMutationError(createMutation.error, "Could not create the GIPI.")}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={createMutation.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={
              selected == null ||
              createMutation.isPending ||
              requestedLines.length === 0 ||
              quantityInvalid ||
              linesQuery.isLoading
            }
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Create GIPI
          </Button>
        </div>
      </div>
    </DocumentModal>
  );
}

function PurchaseOrderPickerRow({
  row,
  selected,
  onSelect,
}: {
  row: SuntronicInvoiceGipiPurchaseOrderRow;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <tr className={cn("cursor-pointer", selected ? "bg-sky-50" : "hover:bg-slate-50")} onClick={onSelect}>
      <td className="px-3 py-2 text-slate-900">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          <span className="min-w-0 break-all">{row.po_number?.trim() || `Purchase order ${row.id}`}</span>
          {row.associated_with_matches ? (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
              From matches
            </span>
          ) : null}
        </div>
      </td>
      <td className="truncate px-3 py-2 text-slate-800">{row.supplier_name ?? EM_DASH}</td>
      <td className="px-3 py-2 text-right tabular-nums">{formatQuantity(row.quantity_remaining_to_invoice)}</td>
    </tr>
  );
}

function PurchaseOrderLineRow({
  row,
  quantityRaw,
  disabled,
  onQuantityChange,
}: {
  row: SuntronicInvoiceGipiPurchaseOrderLineRow;
  quantityRaw: string;
  disabled: boolean;
  onQuantityChange: (value: string) => void;
}): JSX.Element {
  const remaining = toNumber(row.quantity_remaining);
  const parsed = parseQuantityInput(quantityRaw);
  const invalid =
    quantityRaw.trim() !== "" && (parsed == null || parsed < 0 || (parsed != null && parsed > remaining));
  const suggested = toNumber(row.suggested_quantity) > 0;
  return (
    <tr className={cn(suggested ? "bg-sky-50/70" : undefined)}>
      <td className="whitespace-nowrap px-3 py-2 align-top font-mono text-slate-900">
        <div className="min-w-0">
          <div>{row.sku?.trim() || EM_DASH}</div>
          {suggested ? (
            <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 font-sans text-[11px] font-medium text-slate-700">
              Suggested
            </span>
          ) : null}
        </div>
      </td>
      <td className="min-w-[12rem] px-3 py-2 align-top font-sans text-sm leading-snug text-slate-700">
        {row.description?.trim() || EM_DASH}
      </td>
      <td className="whitespace-nowrap px-3 py-2 align-top text-right tabular-nums">{formatQuantity(row.quantity_ordered)}</td>
      <td className="whitespace-nowrap px-3 py-2 align-top text-right tabular-nums">{formatQuantity(row.quantity_remaining)}</td>
      <td className="whitespace-nowrap px-3 py-2 align-top text-right tabular-nums">{formatQuantity(row.matched_quantity)}</td>
      <td className="whitespace-nowrap px-3 py-2 align-top text-right tabular-nums">{formatMoney(row.unit_price)}</td>
      <td className="whitespace-nowrap px-3 py-2 align-top">
        <div className="flex min-w-0 items-center justify-end gap-2">
          <Input
            className={cn("h-9 w-24 text-right tabular-nums", invalid && "border-rose-500")}
            inputMode="decimal"
            disabled={disabled || remaining <= 0}
            value={quantityRaw}
            placeholder={remaining > 0 ? "0" : EM_DASH}
            onChange={(event) => onQuantityChange(event.target.value)}
          />
          {remaining > 0 ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="shrink-0"
              disabled={disabled}
              onClick={() => onQuantityChange(String(remaining))}
            >
              All
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
